/**
 * The answer control for a `predict` question.
 *
 * Extracted rather than duplicated because three screens grade predict
 * questions — the lesson quiz, the boss fight, and patrol — and three copies of
 * a control whose rules include "capitalisation matters, whitespace does not"
 * is three chances to describe those rules differently.
 *
 * Deliberately NOT wearing radio ARIA. The multiple-choice branch beside it
 * uses role="radio"/aria-checked because those controls genuinely are a
 * single-select group; this one is a textbox, and announcing it as a radio
 * button would be a worse lie than saying nothing. Lesson 7.7 makes this
 * argument at length, and someone reading it will check.
 */
export default function PredictInput({
  id,
  value,
  disabled,
  correct,
  expectedOutput,
  onChange,
}: {
  id: string
  value: string
  disabled: boolean
  /** undefined until graded. */
  correct?: boolean
  /** Arrives only with a wrong grade — never before. */
  expectedOutput?: string
  onChange: (next: string) => void
}) {
  const state = correct === undefined ? '' : correct ? 'predict-correct' : 'predict-wrong'

  return (
    <div className="predict">
      <label className="predict-label" htmlFor={`predict-${id}`}>
        What does it print?
      </label>
      <textarea
        id={`predict-${id}`}
        className={['predict-input', state].filter(Boolean).join(' ')}
        rows={3}
        spellCheck={false}
        autoComplete="off"
        autoCapitalize="off"
        autoCorrect="off"
        placeholder="One line per line of output"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      />
      <p className="predict-hint dim">
        Whitespace and indentation are forgiven. Capitalisation is not.
      </p>
      {correct === false && expectedOutput !== undefined && (
        <pre className="predict-expected">{expectedOutput}</pre>
      )}
    </div>
  )
}
